import type {
  FetchAttendanceHistoryParams,
  FetchAvailableCollaboratorsParams,
  FetchManualAttendanceCandidatesParams,
  FetchHrAnalyticsParams,
  FetchHrDashboardParams,
  FetchHrDashboardPlanningStatusParams,
  FetchHrDashboardTrendsParams,
  FetchHrRemoteStorePeriodMetricsParams,
  FetchHrSalesPeaksParams,
  FetchHrStoreWeeklyMetricsParams,
  FetchHrUnavailabilityCalendarParams,
  FetchTransportSegmentsParams,
  FetchWorkShiftsParams,
} from './hr.queries'
import type {
  FetchSmsTrackingListParams,
  FetchSmsTrackingStatsParams,
} from './sms-tracking.queries'
import type { RbacAccessAdminsParams } from '@gozoki/api-types'
export const keys = {
  networkDashboard: {
    all: () => ['network-dashboard'] as const,
    sections: () => [...keys.networkDashboard.all(), 'sections'] as const,
    sectionRoot: (section: NetworkDashboardSection) =>
      [...keys.networkDashboard.sections(), section] as const,
    section: <T>(section: NetworkDashboardSection, params: T) =>
      [...keys.networkDashboard.sectionRoot(section), params] as const,
    availableStores: () => [...keys.networkDashboard.all(), 'available-stores'] as const,
    live: (storeIds: readonly number[], storeAgeFilter: string) =>
      [...keys.networkDashboard.all(), 'live', storeIds, storeAgeFilter] as const,
  },
  featureFlags: {
    all: ['feature-flags'] as const,
    list: () => [...keys.featureFlags.all, 'list'] as const,
  },
  stores: {
    all: ['stores'] as const,
    list: () => [...keys.stores.all, 'list'] as const,
    // Liste AUTHENTIFIÉE (admin) des boutiques adressables — clé distincte de `list` (peuplée par le
    // fetch public non authentifié `/stores` qui exclut REMOTE + boutiques non-online).
    assignable: () => [...keys.stores.all, 'assignable'] as const,
    byId: (id: number | undefined) => [...keys.stores.all, id] as const,
    cameraConfig: {
      summary: (storeId: number | undefined) =>
        [...keys.stores.all, 'camera-config', 'summary', storeId] as const,
      cameras: {
        list: (storeId: number | undefined) =>
          [...keys.stores.all, 'camera-config', 'cameras', 'list', storeId] as const,
        byId: (storeId: number | undefined, cameraId: number | undefined) =>
          [...keys.stores.all, 'camera-config', 'cameras', 'by-id', storeId, cameraId] as const,
      },
    },
    cameraStream: (storeId: number | undefined) =>
      [...keys.stores.all, 'camera-stream', storeId] as const,
  },
  rbac: {
    all: ['rbac'] as const,
    permissions: {
      all: () => [...keys.rbac.all, 'permissions'] as const,
      current: () => [...keys.rbac.permissions.all(), 'current'] as const,
    },
    roles: {
      all: () => [...keys.rbac.all, 'roles'] as const,
      list: () => [...keys.rbac.roles.all(), 'list'] as const,
      withoutHolder: () => [...keys.rbac.roles.all(), 'without-holder'] as const,
      byId: (id: number | undefined) => [...keys.rbac.roles.all(), id] as const,
    },
    /** Écran « Utilisateurs & accès » : agrégats des tuiles, liste des comptes, familles. */
    overview: (usageWindowDays?: number) =>
      [...keys.rbac.all, 'overview', usageWindowDays] as const,
    admins: (params?: RbacAccessAdminsParams) => [...keys.rbac.all, 'admins', params] as const,
    families: () => [...keys.rbac.all, 'families'] as const,
    adminUsage: (adminId: number | undefined, days?: number) =>
      [...keys.rbac.all, 'usage', 'admins', adminId, days] as const,
  },
  hr: {
    all: ['hr'] as const,
    analytics: (params?: FetchHrAnalyticsParams) => [...keys.hr.all, 'analytics', params] as const,
    salesPeaks: (params?: FetchHrSalesPeaksParams) =>
      [...keys.hr.all, 'sales-peaks', params] as const,
    storeWeeklyMetrics: (params: FetchHrStoreWeeklyMetricsParams | undefined) =>
      [...keys.hr.all, 'store-weekly-metrics', params] as const,
    remoteStorePeriodMetrics: (params: FetchHrRemoteStorePeriodMetricsParams | undefined) =>
      [...keys.hr.all, 'remote-store-period-metrics', params] as const,
    dashboard: {
      all: () => [...keys.hr.all, 'dashboard'] as const,
      metricsAll: () => [...keys.hr.dashboard.all(), 'metrics'] as const,
      overview: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'overview', params] as const,
      trends: (params: FetchHrDashboardTrendsParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'trends', params] as const,
      coverage: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'coverage', params] as const,
      planningStatus: (params: FetchHrDashboardPlanningStatusParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'plannings-status', params] as const,
      effectifDetail: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'effectif-detail', params] as const,
      fraisDetail: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'frais-detail', params] as const,
      suiviParBoutique: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.metricsAll(), 'suivi-par-boutique', params] as const,
      availableStores: () => [...keys.hr.dashboard.all(), 'available-stores'] as const,
    },
    absenceCalendar: {
      all: () => [...keys.hr.all, 'absence-calendar'] as const,
      list: (params: FetchHrUnavailabilityCalendarParams | undefined) =>
        [...keys.hr.absenceCalendar.all(), 'list', params] as const,
    },
    collaborators: {
      all: () => [...keys.hr.all, 'collaborators'] as const,
      // La liste (`GET /hr/collaborators`) est passée sur Tuyau : sa clé est construite par
      // @tuyau/query-core sous la forme [[...segments], { request }] — d'où ce préfixe, qui invalide
      // toutes les variantes de `storeId`.
      tuyauList: () => [['collaborators', 'getAll']] as const,
      available: (params?: FetchAvailableCollaboratorsParams) =>
        [...keys.hr.collaborators.all(), 'available', params] as const,
      manualAttendanceCandidates: (params?: FetchManualAttendanceCandidatesParams) =>
        [...keys.hr.collaborators.all(), 'manual-attendance-candidates', params] as const,
      byId: (id: number | undefined) => [...keys.hr.collaborators.all(), id] as const,
      workTimes: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'work-times'] as const,
      unavailabilities: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'unavailabilities'] as const,
      equipment: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'equipment'] as const,
      clothing: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'clothing'] as const,
      contracts: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'contracts'] as const,
    },
    interimAgencies: {
      all: () => [...keys.hr.all, 'interim-agencies'] as const,
      list: (includeArchived?: boolean) =>
        [...keys.hr.interimAgencies.all(), 'list', includeArchived] as const,
    },
    attendanceHistory: {
      all: () => [...keys.hr.all, 'attendance-history'] as const,
      list: (params: FetchAttendanceHistoryParams | undefined) =>
        [...keys.hr.attendanceHistory.all(), 'list', params] as const,
    },
    workShifts: {
      all: () => [...keys.hr.all, 'work-shifts'] as const,
      list: (params?: FetchWorkShiftsParams) =>
        [...keys.hr.workShifts.all(), 'list', params] as const,
      byId: (id: number | undefined) => [...keys.hr.workShifts.all(), id] as const,
    },
    planningConfig: {
      all: () => [...keys.hr.all, 'planning-config'] as const,
    },
    lastOptimization: (params?: { dateStart: string; dateEnd: string }) =>
      [...keys.hr.all, 'last-optimization', params] as const,
    transportSegments: (params?: FetchTransportSegmentsParams) =>
      [...keys.hr.all, 'transport-segments', params] as const,
    smsTracking: {
      all: () => [...keys.hr.all, 'sms-tracking'] as const,
      list: (params: FetchSmsTrackingListParams) =>
        [...keys.hr.smsTracking.all(), 'list', params] as const,
      stats: (params: FetchSmsTrackingStatsParams) =>
        [...keys.hr.smsTracking.all(), 'stats', params] as const,
    },
  },
  storeProjects: {
    // La liste (`GET /store-projects`) est passée sur Tuyau : sa clé est construite par
    // @tuyau/query-core sous la forme [[...segments], { request }] — d'où ce préfixe, qui cible
    // toutes les variantes de la requête.
    tuyauList: () => [['storeProjects', 'index']] as const,
  },
  visioReviews: {
    tuyauOperatorCharts: () => [['visioReviews', 'getOperatorCharts']] as const,
  },
}

export type NetworkDashboardSection = 'sales' | 'losses' | 'categories' | 'top-products' | 'hr'
